/* eslint react/jsx-max-depth: ["error", {max: 5}] -- applies to entire file */
import { useCallback, useMemo, useState } from "react";

import isEmpty from "lodash/isEmpty";

import { WeightTrackingTestID } from "@/constants/TestID";
import {
  VictoryAxis,
  VictoryChartCanvas,
  VictoryLabel,
  VictoryLine,
  VictoryScatter,
} from "@/crossPlatform/victory";
import { Text } from "@/ui/core";
import { TextAttributesMapper } from "@/ui/core/Text";
import { Box } from "@/ui/layout";
import { ColorToken, ColorTokenValues } from "@/ui/theme/tokens";
import { isWeb } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";
import { sortObservationsByDate } from "@/utils/vitals/weight.util";

const GraphSizes = {
  AxisTextGapFromGraph: 8,
  // by default a VictoryChart padding of 0 will completely exclude the VictoryAxis text/labels
  // so we need to explicitly pad to the width of the AxisText as specified here
  AxisTextWidth: 30,
  BackgroundOpacity: 0.5,
  DashGapLength: 4,
  DashLength: 4,
  DataPoint: 3,
  DomainPaddingX: 12,
  DomainPaddingY: 18,
  GridLine: 1,
  GridOpacity: 0.2,
  Height: 96,
  LineWidth: 3,
  TickCount: 4,
  // remove black vertical line for y-axis
  VerticalAxisLine: 0,
};

const GraphColors = {
  AxisText: ColorTokenValues[ColorToken.NeutralSecondary],
  Background: ColorTokenValues[ColorToken.TertiaryMarigoldBackground],
  DataPoint: ColorTokenValues[ColorToken.TertiaryOrangeBase],
  GradientEnd: ColorTokenValues[ColorToken.TertiaryOrangeBase],
  GradientStart: ColorTokenValues[ColorToken.TertiaryMarigoldBase],
  Grid: ColorTokenValues[ColorToken.BackgroundDark],
};

interface WeightTrackingGraphProps {
  data: PatientObservationData[];
  graphHeight?: number;
}

const WeightTrackingGraph = ({
  data,
  graphHeight = GraphSizes.Height,
}: WeightTrackingGraphProps) => {
  // on web, graph is immediately ready. on native, it needs to measure layout/container width first
  const [isReady, setIsReady] = useState(isWeb);
  const handleReady = useCallback(() => setIsReady(true), []);

  // VictoryLine graph infers the direction of the x-axis from the direction of the x data
  // so regardless of how this data was ordered previously, enforce an ascending-by-date order here
  const graphData = useMemo(() => {
    return sortObservationsByDate({ data, isDescending: false }).map((d) => {
      return {
        x: d.observation.observationDatetime,
        y: d.observation.value,
      };
    });
  }, [data]);

  // can only return after React Hooks, as they must be called unconditionally
  if (isEmpty(data)) {
    return null;
  }

  const baseTestID = WeightTrackingTestID.Graph;
  const strokeGradientName = "strokeGradient";
  const { fontFamily, fontSize, letterSpacing, lineHeight } =
    TextAttributesMapper["overline"];

  return (
    <Box gap={8} testID={baseTestID}>
      {/* VictoryChartCanvas owns the platform-specific chart container and linearGradient defs */}
      <VictoryChartCanvas
        domainPadding={{
          x: GraphSizes.DomainPaddingX,
          y: GraphSizes.DomainPaddingY,
        }}
        gradient={{
          endColor: GraphColors.GradientEnd,
          id: strokeGradientName,
          startColor: GraphColors.GradientStart,
        }}
        height={graphHeight}
        onReady={handleReady}
        padding={{
          bottom: 0,
          left: 0,
          right: GraphSizes.AxisTextWidth,
          top: 0,
        }}
        style={{
          background: {
            fill: GraphColors.Background,
            opacity: GraphSizes.BackgroundOpacity,
          },
        }}
      >
        <VictoryAxis
          dependentAxis
          orientation="right"
          style={{
            axis: {
              stroke: GraphSizes.VerticalAxisLine,
            },
            grid: {
              opacity: GraphSizes.GridOpacity,
              stroke: GraphColors.Grid,
              strokeDasharray: [
                GraphSizes.DashLength,
                GraphSizes.DashGapLength,
              ].toString(),
              strokeWidth: GraphSizes.GridLine,
            },
          }}
          // tickCount is not guaranteed to be exact, but approximate
          tickCount={GraphSizes.TickCount}
          tickFormat={(t: number) => Math.round(t)}
          tickLabelComponent={
            <VictoryLabel
              dx={GraphSizes.AxisTextGapFromGraph}
              style={{
                fill: GraphColors.AxisText,
                fontFamily,
                fontSize,
                letterSpacing,
                lineHeight,
              }}
              textAnchor="middle"
            />
          }
        />
        {/* on native, do not render until the chart overalli is ready */}
        {isReady ? (
          <VictoryLine
            data={graphData}
            style={{
              data: {
                stroke: `url(#${strokeGradientName})`,
                strokeWidth: GraphSizes.LineWidth,
              },
            }}
          />
        ) : null}
        <VictoryScatter
          data={graphData}
          size={GraphSizes.DataPoint}
          style={{
            data: { fill: GraphColors.DataPoint },
          }}
        />
      </VictoryChartCanvas>
      <Text
        color={ColorToken.NeutralSecondary}
        testID={joinTestIDs(baseTestID, "duration")}
        variant="caption"
      >
        Last 4 weeks
      </Text>
    </Box>
  );
};

export default WeightTrackingGraph;
